import React from "react";
import ComplexText from "@ui/complex-text";
import Badge from "@ui/badge";
import Tooltip from "@ui/tooltip";
import { IconInfo } from "@ui/icons";
import {
    getFontSizeCss,
    getTextColourCss,
    getFontSizeStyle,
    getBorderColourCss,
    getBgColorCss,
    getBorderRadiusSizeCss
} from "@helpers/css-convertions";
import CellLink from "./cell-link";

const TooltipCmp = ({ data }) => {
    return (
        <Tooltip content={<ComplexText values={data.content} />}>
            <IconInfo className="w-4 h-4" />
        </Tooltip>
    );
};

const SubCells = ({ data }) => {
    if (!data) return null;
    return (
        <div>
            {data.map((cell, index) => (
                <Cell key={index} data={cell} />
            ))}
        </div>
    );
};

const Cell = ({ data }) => {
    const colourCss = getTextColourCss(data.colour);
    const fontSizeStyle = getFontSizeStyle(data.fontSize);
    const style = {
        width: data.widthInPercent > 0 ? `${data.widthInPercent}%` : null,
        paddingLeft: data.padding?.left > 0 ? `${data.padding.left}px` : null,
        paddingRight:
            data.padding?.right > 0 ? `${data.padding.right}px` : null,
        paddingTop: data.padding?.top > 0 ? `${data.padding.top}px` : null,
        paddingBottom:
            data.padding?.bottom > 0 ? `${data.padding.bottom}px` : null,
        ...fontSizeStyle,
    };

    const alignCss = data.align
        ? data.align === "Right"
            ? "justify-end"
            : data.align === "Center"
            ? "justify-center"
            : "justify-start"
        : "";
    
    const alignLargeCss = data.alignLarge 
        ? data.alignLarge === "Right"
            ? "md:justify-end"
            : data.align === "Center"
            ? "md:justify-center"
            : "md:justify-start"
        : "";

    const textAlignCss = data.textAlign
        ? data.textAlign === "Right"
            ? "text-right"
            : data.textAlign === "Center"
            ? "text-center"
            : "text-left"
        : "";

    const imageStyle = data.image
        ? {
              width: data.image.width ? `${data.image.width}px` : "",
              height: data.image.height ? `${data.image.height}px` : "",
          }
        : {};

    const postImageStyle = data.postImage
        ? {
              width: data.postImage.width ? `${data.postImage.width}px` : "",
              height: data.postImage.height ? `${data.postImage.height}px` : "",
          }
        : {};

    const imageCmpStyle = data.image
        ? {
              borderRadius: data.image.borderRadius
                  ? `${data.image.borderRadius}px`
                  : "",
          }
        : {};

    return (
        <div
            className={`flex items-center ${alignCss} ${alignLargeCss} ${textAlignCss} gap-2 ${colourCss}`}
            style={style}
        >
            {data.image && (
                <div className="inline-block" style={imageStyle}>
                    <img
                        src={data.image.url}
                        className="w-full"
                        alt={data.image.alt}
                        style={imageCmpStyle}
                    />
                </div>
            )}
            {data.text && !data.smallText && (
                <div className="inline-block">
                    <ComplexText values={data.text} />
                </div>
            )}
            {data.smallText && !data.text && (
                <div className="inline-block text-sm">
                    <ComplexText values={data.smallText} />
                </div>
            )}
            {data.smallText && data.text && (
                <div className="inline-block">
                    <div className="inline-block">
                        <ComplexText values={data.text} />
                    </div>
                    <div className="text-xs inline-block">
                        <ComplexText values={data.smallText} />
                    </div>
                </div>
            )}
            {data.tooltip && <TooltipCmp data={data.tooltip} />}
            {data.badge && <Badge data={data.badge} />}
            {data.postImage && (
                <div className="inline-block" style={postImageStyle}>
                    <img
                        src={data.postImage.url}
                        className="w-full"
                        alt={data.postImage.alt}
                    />
                </div>
            )}
            {data.link && <CellLink data={data.link} />}
            {data.cells && <SubCells data={data.cells} />}
        </div>
    );
};

const buildRowBorderCss = (topColour, bottomColour) => {
    const css = [];

    if (topColour) css.push("border-t", getBorderColourCss(topColour));
    if (bottomColour) css.push("border-b", getBorderColourCss(bottomColour));

    return css.join(" ");
};

const Row = ({ data }) => {
    if (!data?.cells) return;

    const style = {
        marginTop: data.marginTop ?? 0,
        marginBottom: data.marginBottom ?? 0,
        marginLeft: data.marginLeft ?? 0,
        marginRight: data.marginRight ?? 0,
        paddingBottom: data.paddingBottom ?? 0,
        paddingTop: data.paddingTop ?? 0,
        borderTopRightRadius: data.topRadius
        ? getBorderRadiusSizeCss(data.topRadius)
        : null,
    borderBottomRightRadius: data.bottomRadius
        ? getBorderRadiusSizeCss(data.bottomRadius)
        : null,
    borderTopLeftRadius: data.topRadius
        ? getBorderRadiusSizeCss(data.topRadius)
        : null,
    borderBottomLeftRadius: data.bottomRadius
        ? getBorderRadiusSizeCss(data.bottomRadius)
        : null,
    };

    const borderCss = buildRowBorderCss(
        data.borderTopColour,
        data.borderBottomColour
    );

    const bgColor = getBgColorCss(data.backgroundColour);
    const itemsAlignCss = data.alignTop ? "items-start" : "items-center";
    const flexCss = data.stackCellsOnSmallScreen
        ? "flex flex-col md:flex-row"
        : "flex";

    return (
        <div
            className={`${flexCss} justify-between ${bgColor} ${borderCss} ${itemsAlignCss}`}
            style={style}
        >
            {data.cells.map((cell, index) => {
                return <Cell key={index} data={cell} />;
            })}
        </div>
    );
};

const TabularDataWidget = ({ data }) => {
    if (!data?.rows) return null;

    const css = data.containerType
        ? data.containerType === "Card"
            ? `border rounded-md border-skin-divider px-6`
            : data.containerType === "CardShadow"
            ? `border rounded-md border-skin-divider shadow-md px-6`
            : ""
        : "";


    return (
        <div className={`${css}`}>
            {data.rows.map((row, index) => {
                return row && <Row key={index} data={row} />;
            })}
        </div>
    );
};

export default TabularDataWidget;
